// TopActionBar.js
// Barra sticky no topo da content area. Presente em todas as pages privadas.
//
// Left:  hamburguer (mobile only) para abrir sidebar drawer + breadcrumbs
// Right: Sincronizar + Backup + SyncView
//
// Dashboard Geral foi REMOVIDO daqui em 2026-05-10 (redundante com a sidebar).
//
// Sincronizar: igual a Home.initSync() — lê downloadFarms do localStorage,
//   startLoading, forceSync + pullFarms com farm IDs, endLoading, dispara
//   CustomEvent 'progerar:sync-completed' (pages que precisam refresh
//   escutam, ex: Home.getCowsByFarmId)
// Backup: igual a Home.handleToBackup() — lê selectedEstacaoDeMonta do
//   localStorage, busca user.nomeCompleto via getLoggedUser, monta filename
//   "Backup <user> EM <em>.zip" e chama sync.downloadBackup

import React, { useState } from 'react'
import {
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Box,
  Breadcrumbs,
  Divider,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import MenuIcon from '@mui/icons-material/Menu'
import SyncIcon from '@mui/icons-material/Sync'
import BackupIcon from '@mui/icons-material/CloudDownload'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'

import SyncView from '../SyncView'
import * as sync from '../../syncWorker'
import { getLoggedUser } from '../../redux/auth/actions'
import { startLoading, endLoading } from '../../redux/screen/actions'
import { LocalStorageHelper } from '../../utils/localStorageHelper'
import hasInternet from '../../utils/recognizeInternetConnection'
import { AVAILABLE_ENTITIES, utils } from '../../database'
import { formatName, USUARIO } from '../../utils/formHelper'
import { recordError, recordEvent } from '../../observability/events'
import { usePageLayout } from './PageLayoutProvider'

const SYNC_COMPLETED_EVENT = 'progerar:sync-completed'

// ── Breadcrumbs no TopActionBar ─────────────────────────────────────────────
// Renderizado entre o hamburger e o cluster de ações. Cresce com flex:1 mas
// trunca o leaf (com ellipsis) e colapsa intermediários no mobile pra NUNCA
// empurrar as ações pra fora da viewport.
//
// Mobile (xs): mostra só ProGERAR > <leaf truncado>. Itens intermediários
// somem (rota é redundante com a sidebar selecionada).
// Desktop (md+): trail completo via MUI Breadcrumbs (maxItems=4 colapsa
// caminhos muito profundos com "..." automático).
function PageBreadcrumbs({ pageInfo, isMobile }) {
  const paths = pageInfo?.paths
  const fallbackTitle = pageInfo?.title

  const items = Array.isArray(paths) ? paths.filter(Boolean) : []
  if (items.length === 0 && fallbackTitle) {
    items.push({ label: fallbackTitle })
  }
  // Sem nada pra mostrar — ainda assim ocupa o flex pra garantir alinhamento
  // do cluster direito (sem leaf, fica só o root clicável).
  const trail = [{ label: 'ProGERAR', route: '/' }, ...items]
  const lastIndex = trail.length - 1

  const linkSx = {
    color: 'text.secondary',
    textDecoration: 'none',
    fontWeight: 500,
    fontSize: { xs: '0.8125rem', md: '0.875rem' },
    whiteSpace: 'nowrap',
    '&:hover': { color: 'primary.main', textDecoration: 'underline' },
  }

  const leafSx = {
    color: 'text.primary',
    fontWeight: 600,
    fontSize: { xs: '0.875rem', md: '0.9375rem' },
    // Trunca com ellipsis. minWidth:0 no parent + overflow:hidden aqui.
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
    maxWidth: '100%',
  }

  const renderItem = (item, idx) => {
    const isLeaf = idx === lastIndex
    if (isLeaf) {
      return (
        <Box
          key={`bc_${idx}`}
          component='span'
          aria-current='page'
          sx={leafSx}
          title={item.label}
        >
          {item.label}
        </Box>
      )
    }
    if (item.route) {
      return (
        <Box key={`bc_${idx}`} component={Link} to={item.route} sx={linkSx}>
          {item.label}
        </Box>
      )
    }
    return (
      <Box key={`bc_${idx}`} component='span' sx={linkSx}>
        {item.label}
      </Box>
    )
  }

  // Mobile: só root + leaf (descarta intermediários). Reduz risco do
  // breadcrumb empurrar as ações pra fora do viewport em telas estreitas.
  const visibleTrail =
    isMobile && trail.length > 2 ? [trail[0], trail[lastIndex]] : trail

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        mx: { xs: 1, md: 2 },
        overflow: 'hidden',
      }}
    >
      <Breadcrumbs
        aria-label='breadcrumb'
        maxItems={isMobile ? 2 : 4}
        separator={
          <NavigateNextIcon fontSize='small' sx={{ color: 'text.disabled' }} />
        }
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          '& .MuiBreadcrumbs-ol': {
            flexWrap: 'nowrap',
            alignItems: 'center',
            minWidth: 0,
          },
          // Último <li> recebe o resto do espaço pra truncar o leaf
          '& .MuiBreadcrumbs-li:last-of-type': {
            minWidth: 0,
            overflow: 'hidden',
          },
          '& .MuiBreadcrumbs-separator': {
            mx: { xs: 0.5, md: 1 },
            flexShrink: 0,
          },
        }}
      >
        {visibleTrail.map(renderItem)}
      </Breadcrumbs>
    </Box>
  )
}

function TopActionBar({
  onMobileMenuOpen,
  dispatchStartLoading,
  dispatchEndLoading,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [syncing, setSyncing] = useState(false)
  const { pageInfo } = usePageLayout()

  // ── Equivalente a Home.initSync ──────────────────────────────────────────
  const handleSync = async () => {
    if (syncing) return
    setSyncing(true)

    const startedAt = Date.now()
    let farmsIds = []
    const downloadFarmsStr = localStorage.getItem('downloadFarms')
    if (downloadFarmsStr) {
      try {
        const downloadFarms = JSON.parse(downloadFarmsStr)
        farmsIds = downloadFarms.map((f) => f.id)
      } catch (e) {
        recordError(e, {
          category: 'sync',
          action: 'parse_download_farms_failed',
        })
      }
    }

    recordEvent({
      level: 'info',
      category: 'sync',
      action: 'sync_geral_started',
      payload: {
        farmsCount: farmsIds.length,
        hasInternet: hasInternet(),
        source: 'TopActionBar',
      },
    })

    if (!hasInternet()) {
      recordEvent({
        level: 'warn',
        category: 'sync',
        action: 'sync_geral_no_internet',
      })
      setSyncing(false)
      return
    }

    dispatchStartLoading()
    try {
      if (farmsIds.length > 0) {
        try {
          await sync.forceSync(false, true)
        } catch (pushErr) {
          recordEvent({
            level: 'warn',
            category: 'sync',
            action: 'sync_geral_push_failed',
            payload: { message: pushErr?.message },
          })
        }
        await sync.pullFarms(farmsIds, {}, false, true, true)
      } else {
        await sync.forceSync()
      }

      recordEvent({
        level: 'info',
        category: 'sync',
        action: 'sync_geral_completed',
        payload: {
          farmsCount: farmsIds.length,
          durationMs: Date.now() - startedAt,
        },
      })

      // Notifica pages que precisam refresh local (ex: Home.getCowsByFarmId).
      // Mecanismo desacoplado: pages assinam window event no componentDidMount.
      window.dispatchEvent(new CustomEvent(SYNC_COMPLETED_EVENT))
    } catch (err) {
      recordError(err, {
        category: 'sync',
        action: 'sync_geral_failed',
        payload: { farmsCount: farmsIds.length, source: 'TopActionBar' },
      })
    } finally {
      dispatchEndLoading()
      setSyncing(false)
    }
  }

  // ── Equivalente a Home.handleToBackup ─────────────────────────────────────
  const handleBackup = async () => {
    try {
      const selectedEMId = LocalStorageHelper.get('selectedEstacaoDeMonta')

      // Busca dados pra montar o filename igual Home.handleToBackup
      const logged = await getLoggedUser()
      const user = logged?.userId
        ? await utils.getElement(AVAILABLE_ENTITIES.USERS, logged.userId)
        : null
      const estacaoMontaList = await utils.getDomainValuesBy('Estação de Monta')
      const selectedEM = estacaoMontaList?.find((em) => em.id === selectedEMId)

      const userName = user?.nomeCompleto
        ? formatName(user.nomeCompleto, USUARIO)
        : 'Usuário'
      const emLabel = selectedEM ? ` EM ${selectedEM.valor}` : ' EM Geral'
      const filename = `Backup ${userName}${emLabel}.zip`

      sync.downloadBackup(filename, selectedEMId ?? null)
    } catch (err) {
      recordError(err, {
        category: 'sync',
        action: 'backup_failed',
        payload: { source: 'TopActionBar' },
      })
      // Fallback: backup sem filename customizado
      sync.downloadBackup()
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  const actions = [
    {
      key: 'sync',
      label: 'Sincronizar',
      icon: syncing ? (
        <CircularProgress size={18} thickness={5} />
      ) : (
        <SyncIcon />
      ),
      tooltip: syncing ? 'Sincronizando...' : 'Forçar sincronização',
      onClick: handleSync,
      disabled: syncing,
    },
    {
      key: 'backup',
      label: 'Backup',
      icon: <BackupIcon />,
      tooltip: 'Baixar backup local',
      onClick: handleBackup,
    },
  ]

  const pillSx = {
    // '8px' literal — número em sx é multiplicado por shape.borderRadius (6),
    // dava 48px = pill/chip. String '8px' = 8px reais, alinhado com AppButton/SyncView.
    borderRadius: '8px',
    px: 1.75,
    py: 0.5,
    textTransform: 'none',
    fontWeight: 500,
    color: 'text.secondary',
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    '&:hover': {
      color: 'primary.main',
      borderColor: 'primary.main',
      bgcolor: 'rgba(234, 93, 13, 0.04)',
    },
    '&.Mui-disabled': {
      color: 'text.disabled',
      borderColor: 'divider',
    },
  }

  const pillIconSx = {
    minWidth: 44,
    minHeight: 44,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '8px',
    color: 'text.secondary',
    '&:hover': {
      color: 'primary.main',
      borderColor: 'primary.main',
      bgcolor: 'rgba(234, 93, 13, 0.04)',
    },
  }

  return (
    <AppBar
      position='sticky'
      color='default'
      elevation={0}
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        zIndex: (t) => t.zIndex.appBar,
        '@media print': { display: 'none' },
      }}
    >
      <Toolbar
        variant='dense'
        disableGutters
        sx={{
          // Match com a logo row da AppSidebar (Box minHeight 60).
          // Antes era 56 — criava degrau visual de 4px entre sidebar e topbar.
          minHeight: 60,
          // !important pra vencer o default do MuiToolbar-dense (48px) que
          // pode estar mais específico em alguns navegadores.
          '&.MuiToolbar-root': { minHeight: 60 },
        }}
      >
        {/* Inner wrapper constrained — alinha com AppContent (max 1440px).
            O AppBar/Toolbar continua full-width (efeito visual de barra),
            mas os children ficam dentro do mesmo grid que o conteúdo abaixo. */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 1440,
            mx: 'auto',
            px: { xs: 1.5, md: 3 },
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Hamburger — mobile only */}
          <IconButton
            edge='start'
            onClick={onMobileMenuOpen}
            aria-label='Abrir menu'
            sx={{
              display: { md: 'none' },
              mr: 0.5,
              minWidth: 44,
              minHeight: 44,
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Breadcrumb cluster — flex:1, minWidth:0 garante que o cluster
              direito (ações) NUNCA é empurrado pra fora da tela. O nome da
              página (leaf) trunca com ellipsis quando muito longo. */}
          <PageBreadcrumbs pageInfo={pageInfo} isMobile={isMobile} />

          {/* Right cluster — alinhamento à direita garantido pelo flex:1
              do breadcrumb. Gap interno consistente (1.5 = 12px) entre pills.
              Divider sutil separa ações do indicador de status (SyncView). */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, md: 1.5 },
              flexShrink: 0,
            }}
          >
            {actions.map((action) => (
              <Tooltip key={action.key} title={action.tooltip} arrow>
                <span style={{ display: 'inline-flex' }}>
                  {isMobile ? (
                    <IconButton
                      onClick={action.onClick}
                      size='small'
                      disabled={action.disabled}
                      sx={pillIconSx}
                    >
                      {action.icon}
                    </IconButton>
                  ) : (
                    <Button
                      onClick={action.onClick}
                      startIcon={action.icon}
                      size='small'
                      disabled={action.disabled}
                      sx={pillSx}
                    >
                      {action.label}
                    </Button>
                  )}
                </span>
              </Tooltip>
            ))}

            {/* Divider vertical sutil entre ações e SyncView */}

            {/* SyncView — status indicator (passivo) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <SyncView />
            </Box>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  )
}

const mapDispatchToProps = (dispatch) => ({
  dispatchStartLoading: () => dispatch(startLoading()),
  dispatchEndLoading: () => dispatch(endLoading()),
})

export default connect(null, mapDispatchToProps)(TopActionBar)

export { SYNC_COMPLETED_EVENT }
